/**
 * Notification slice: inbox list for drawer, unread count for bell.
 * Fetched list is kept in React Query; this slice holds unreadCount and optional recent items for real-time updates.
 */

import { createSlice, PayloadAction } from '@reduxjs/toolkit'
import type { NotificationItem } from '@/modules/notifications/types/notification.types'
import { logout } from './auth.slice'

interface NotificationState {
  unreadCount: number
  /** Recent items for drawer (prepended on websocket); full list from API. */
  recentItems: NotificationItem[]
}

const initialState: NotificationState = {
  unreadCount: 0,
  recentItems: [],
}

const notificationSlice = createSlice({
  name: 'notification',
  initialState,
  reducers: {
    setUnreadCount: (state, action: PayloadAction<number>) => {
      state.unreadCount = action.payload
    },
    addNotification: (state, action: PayloadAction<NotificationItem>) => {
      if (state.recentItems.find((n) => n.id === action.payload.id)) return
      state.recentItems = [action.payload, ...state.recentItems].slice(0, 50)
      state.unreadCount = Math.max(0, state.unreadCount) + 1
    },
    prependNotifications: (state, action: PayloadAction<NotificationItem[]>) => {
      const existingIds = new Set(state.recentItems.map((n) => n.id))
      const newOnes = action.payload.filter((n) => !existingIds.has(n.id))
      state.recentItems = [...newOnes, ...state.recentItems].slice(0, 50)
    },
    markReadInStore: (state, action: PayloadAction<string>) => {
      const id = action.payload
      const item = state.recentItems.find((n) => n.id === id)
      if (item && !item.isRead) {
        item.isRead = true
        state.unreadCount = Math.max(0, state.unreadCount - 1)
      }
    },
    resetNotifications: () => initialState,
  },
  extraReducers: (builder) => {
    builder.addCase(logout, () => initialState)
  },
})

export const {
  setUnreadCount,
  addNotification,
  prependNotifications,
  markReadInStore,
  resetNotifications,
} = notificationSlice.actions
export default notificationSlice.reducer
